/*
 * Copyright (c) 2018-2019, F5 Networks, Inc. All rights reserved.
 *
 * No part of this software may be reproduced or transmitted in any
 * form or by any means, electronic or mechanical, for any purpose,
 * without express written permission of F5 Networks, Inc.
 */
import { scError, PROGRESS_STATUS } from './Model';

class StatusCheck {
    onComplete?: () => void;
    onError?: (httpErr: number, apmErr: number) => void;
    onProgress?: (s: string) => void;

    private xhr: XMLHttpRequest | null;
    private actionReceived: boolean;
    private respLength: number;
    private isAborted: boolean;

    constructor() {
        this.xhr = null;
        this.actionReceived = false;
        this.respLength = 0;
        this.isAborted = false;
    }

    abort() {
        this.respLength = 0;
        this.actionReceived = false;
        this.isAborted = true;
        if (this.xhr) {
            try {
                this.xhr.abort();
            } catch (e) {
                //
            }
            this.xhr = null;
        }
    }

    run(uri: string): void {
        this.abort();

        if (!uri) {
            uri = '/my.status';
        }

        this.isAborted = false;
        const xhr = new XMLHttpRequest();
        xhr.addEventListener('progress', () => this.updateProgress(xhr), false);
        xhr.addEventListener('load', () => this.transferComplete(xhr), false);
        xhr.addEventListener('error', () => this.transferFailed(xhr), false);

        this.fireOnProgress(PROGRESS_STATUS.obtaining_status);

        try {
            xhr.open('GET', uri, true);
            xhr.send(null);
            this.xhr = xhr;
        } catch (e) {
            // statusCheckDebug("Failed to perfrom XMLHttpRequest:" + e.message)
            this.fireOnError(0, scError.failedToSendRequest);
            return;
        }
    }

    xhrFinished(): boolean {
        return this.xhr !== null && this.xhr.readyState === 4;
    }

    private fireOnComplete(): void {
        if (this.onComplete) {
            this.onComplete();
        }
    }

    private fireOnError(httpErr: number, apmErr: number = 0): void {
        if (this.onError) {
            this.onError(httpErr, apmErr);
        }
    }

    private fireOnProgress(s: string): void {
        if (this.onProgress) {
            this.onProgress(s);
        }
    }

    // this function expectes array of objects which contain
    // status udpates (see PROGRESS_STATUS), this array may contain empty objects
    // e.g. [{"status": {"caption":"awaiting_connection", }, {}]
    private handleUpdates(updates: any[]): void {
        for (const update of updates) {
            if (Object.keys(update).length === 0) {
                // empty object
                continue;
            }

            if (update.status.caption === PROGRESS_STATUS.done) {
                this.actionReceived = true;
                this.fireOnComplete();
                return;
            } else if (update.status.caption === PROGRESS_STATUS.expired) {
                this.actionReceived = true;
                this.abort();
                this.fireOnError(0, scError.sessionExpired);
                return;
            }

            this.fireOnProgress(update.status.caption);
        }
    }

    private updateProgress(xhr: XMLHttpRequest): void {
        if (this.actionReceived) {
            return;
        }

        let r = xhr.response.substr(this.respLength);
        if (r.length === 0) {
            return;
        }

        let s: any[];
        try {
            // trick: server sends every update with comma appended
            // this allows us to handle cases when two or more updates come
            // at same time
            r = '[' + r + '{}]';
            s = JSON.parse(r);
            this.respLength = xhr.response.length;
        } catch (e) {
            // statusCheckDebug("Failed to parse: " + r);
            return;
        }

        this.handleUpdates(s);
    }

    private transferComplete(xhr: XMLHttpRequest): void {
        // handle uprocessed data if any
        // if communication is fast transferComplete is fired without updateProgress
        this.updateProgress(xhr);

        if (this.actionReceived) {
            return;
        }

        this.actionReceived = true;

        if (xhr.status === 200) {
            // request is completed but actionReceived is not set - very strange
            // let's fire onComplete to reload a page
            this.fireOnComplete();
            return;
        }

        // Definitely unexpected
        this.fireOnError(xhr.status);
    }

    private transferFailed(xhr: XMLHttpRequest): void {
        if (this.actionReceived) {
            // ignore possible failure happened after completion
            return;
        }

        if (!this.isAborted) {
            this.fireOnError(0, scError.transferFailed);
        }
    }
}
export { StatusCheck };
